import React, { ReactElement, useMemo } from 'react'
import PageHeader from '../../components/PageHeader'
import { RADOM_COLORS } from '../../util/Constants'
import Tooltip from '../../components/Tooltip'
import { AccountServicesType, IAccountUsage, MonthlyVolumeType, PaymentCounterpartiesType, ProductsUsageType, RadomDiscover } from '../../state/Radom'
import Checkbox from '../../components/Checkbox'
import { PrimaryButton, SecondaryButton } from '../../components/Button'
import { InputLabel, OptionalLabel, TextInputWithLabel } from '../../components/Input'
import Dropdown, { DropdownItem } from '../../components/Dropdown'
import { errorToast, highlightInvalidFields, sanitizeName } from '../../util/Util'
import FileInput from '../../components/FileInput'
import { VerificationRequirementError } from '../../components/VerificationRequirementError'
import SearchMultiselect from '../../components/SearchMultiselect'
interface IProps {
  state: IAccountUsage & { logoFile?: File, logoUrl?: string }
  isSubmitting: boolean
  getError: (fieldId: string) => string | undefined
  onUpdate: (value: any) => void
  onSubmit: () => void
}

const AccountUsage = ({ state, isSubmitting, getError, onUpdate, onSubmit }: IProps): ReactElement => {
  const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const fileList = event.target.files

    if (fileList && fileList.length > 0) {
      const file = fileList[0]
      if (file.type.startsWith('image/')) {
        onUpdate({ logoFile: file })
      } else {
        errorToast('Invalid file type. Please upload an image file.')
      }
    }
  }

  const productsUsageItems = useMemo(
    () => Object.entries(ProductsUsageType).map(([key, value]) => ({
      key,
      value
    })), [])

  const monthlyVolumeTypes = useMemo(
    () => Object.entries(MonthlyVolumeType).map(([key, value]) => (
      <DropdownItem key={key} onClick={() => {
        onUpdate({ monthlyVolume: value })
      }}>
        {value}
      </DropdownItem>
    )), [])

  const paymentCounterpartiesTypes = useMemo(
    () => Object.entries(PaymentCounterpartiesType).map(([key, value]) => (
      <DropdownItem key={key} onClick={() => {
        onUpdate({ paymentCounterparties: key })
      }}>
        {value}
      </DropdownItem>
    )), [])

  const radomDiscoverTypes = useMemo(
    () => Object.entries(RadomDiscover).map(([key, value]) => (
      <DropdownItem key={key} onClick={() => {
        onUpdate({ discoverRadom: value })
      }}>
        {value}
      </DropdownItem>
    )), [])

  const isValid = useMemo(() => {
    if (state.accountServices === AccountServicesType.crypto_only) {
      return !!state.accountServices &&
        !!state.productsUsage &&
        !!state.monthlyVolume &&
        !!state.displayName
    } else {
      return !!state.accountServices &&
      !!state.productsUsage &&
      !!state.monthlyVolume &&
      !!state.paymentCounterparties &&
      !!state.displayName
    }
  }, [state])
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, fontSize: 14, paddingBottom: 40 }}>
      <div style={{ marginBottom: 20 }}>
        <PageHeader title="Account Usage" subTitle="Let us know how you intend to use your Radom account." />
      </div>
      <div style={{ display: 'flex', gap: 17, flexDirection: 'column' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <span style={{ fontSize: 20 }}>Account services</span>
          <span style={{ display: 'flex', alignItems: 'center', fontSize: 14, color: RADOM_COLORS.GRAY_DARKEST, gap: 5 }}>
            What services would you like access to? <Tooltip message={'Choose the services you want to use'} />
          </span>
          <VerificationRequirementError message={getError('accountServices')} />
        </div>
        <div style={{ fontSize: 14, display: 'flex', justifyContent: 'space-between', gap: 20 }}>
          <SecondaryButton
            disabled={isSubmitting}
            onClick={() => {
              onUpdate({ accountServices: AccountServicesType.crypto_only })
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              border: 0,
              padding: 5,
              color: state.accountServices === AccountServicesType.crypto_only
                ? RADOM_COLORS.BLACK
                : RADOM_COLORS.GRAY_DARKEST
            }}
            type='button'>
            <Checkbox size={30} color={RADOM_COLORS.BLUE4}
              checked={state.accountServices === AccountServicesType.crypto_only} />
            <div style={{ display: 'flex', gap: 4, flexDirection: 'column', alignItems: 'flex-start' }}>
              <span>Crypto Only</span>
              <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>
                  Core crypto features with lighter requirements
              </span>
            </div>
          </SecondaryButton>

          <SecondaryButton
            disabled={isSubmitting}
            onClick={() => {
              onUpdate({ accountServices: AccountServicesType.crypto_and_fiat })
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              border: 0,
              padding: 5,
              color: state.accountServices === AccountServicesType.crypto_and_fiat
                ? RADOM_COLORS.BLACK
                : RADOM_COLORS.GRAY_DARKEST
            }}
            type='button'>
            <Checkbox size={30} color={RADOM_COLORS.BLUE4}
              checked={state.accountServices === AccountServicesType.crypto_and_fiat} />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
              <span>Crypto & Fiat</span>
              <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>
                  Full crypto and fiat features with enhanced requirements
              </span>
            </div>
          </SecondaryButton>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 500 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <InputLabel>What products do you plan to use?</InputLabel>
            <Tooltip message='Choose the products you want to use' />
          </div>
          <VerificationRequirementError message={getError('productsUsage')} />
          <SearchMultiselect
            placeholder="Select products"
            dropdownPlaceholder="No products found"
            showMultiSelectIndicator={true}
            itemArray={productsUsageItems}
            selectedItems={state.productsUsage
              ? state.productsUsage.split(',').map(key => ({
                key: key.trim(),
                value: ProductsUsageType[key.trim() as keyof typeof ProductsUsageType] || key.trim()
              }))
              : []}
            setSelectedItems={(items) => {
              const keysString = items.map(item => item.key).join(',')
              onUpdate({ productsUsage: keysString })
            }}
            keyExtractor={(item) => item.key}
            labelExtractor={(item) => item.value}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 500 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <InputLabel>Expected monthly volume in USD (crypto + fiat)</InputLabel>
            <Tooltip message='Choose the expected monthly volume in USD (crypto + fiat)' />
          </div>
          <VerificationRequirementError message={getError('monthlyVolume')} />
          <Dropdown
            key={state.monthlyVolume}

            selectedContent={
              MonthlyVolumeType[state.monthlyVolume as keyof typeof MonthlyVolumeType] ||
      state.monthlyVolume ||
      'Input tier'
            }
            dropdownContent={monthlyVolumeTypes}
          />
        </div>

        {state.accountServices === AccountServicesType.crypto_and_fiat && <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 500 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <InputLabel>Expected payment counterparties</InputLabel>
            <Tooltip message='Choose the payment counterparties you expect to work with' />
          </div>
          <VerificationRequirementError message={getError('paymentCounterparties')} />
          <Dropdown
            key={state.productsUsage}
            selectedContent={PaymentCounterpartiesType[state.paymentCounterparties] || 'Select expected counterparties'}
            dropdownContent={paymentCounterpartiesTypes}
          />
        </div>}
      </div>
      <div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 500 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span style={{ fontSize: 20 }}>Account branding</span>
            <span style={{ display: 'flex', alignItems: 'center', fontSize: 14, color: RADOM_COLORS.GRAY_DARKEST, gap: 5 }}>
            Personalise your account <Tooltip message={'Choose how you want your account to be branded'} />
            </span>
          </div>
          <TextInputWithLabel
            required
            label="Account display name / trading name"
            tooltip="Legal or trading name displayed to your customers"
            placeholder="Enter your account display name"
            errorMessage={getError('displayName')}
            value={state.displayName}
            onChange={(event) => {
              const eventTarget = event.target as HTMLInputElement
              const sanitizedValue = sanitizeName(eventTarget.value)
              onUpdate({ displayName: sanitizedValue })
            }}
          />
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 10 }}>
              <InputLabel>
                Upload your logo
              </InputLabel>
              <OptionalLabel>Optional</OptionalLabel>
              <Tooltip message='Upload your logo image (JPG, PNG, etc.)' />
            </div>
            { getError('logo') && <div style={{ marginBottom: 10 }}>
              <VerificationRequirementError message={getError('logo')} />

            </div>
            }
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <FileInput
                value={state.logoFile}
                onChange={onFileChange}
              />
              {state.logoFile && (
                <img src={URL.createObjectURL(state.logoFile)} alt="Account Logo" style={{ width: 'auto', height: 70, borderRadius: 5 }} />
              )}
              {!state.logoFile && state.logoUrl && (
                <img src={state.logoUrl} alt="Account Logo" style={{ width: 'auto', height: 70, borderRadius: 5 }} />
              )}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 500 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <InputLabel>How did you discover Radom</InputLabel>
              <OptionalLabel>Optional</OptionalLabel>
              <Tooltip message='Select how you discovered Radom' />
            </div>
            <VerificationRequirementError message={getError('discoverRadom')} />
            <Dropdown
              key={state.discoverRadom}

              selectedContent={
                RadomDiscover[state.discoverRadom as keyof typeof radomDiscoverTypes] ||
      state.discoverRadom ||
      'Select how you discovered Radom'
              }
              dropdownContent={radomDiscoverTypes}
            />
          </div>
        </div>
      </div>
      <PrimaryButton
        style={{ width: 500 }}
        disabled={isSubmitting}
        isLoading={isSubmitting}
        onClick={() => {
          if (isValid) {
            onSubmit()
          } else {
            highlightInvalidFields()
          }
        }}>
        Continue
      </PrimaryButton>
    </div>
  )
}

export default AccountUsage
